import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import Footer from "../../components/Footer";
import DataTable from "../../components/DataTable";
import { api } from "../../axios/api";
import { Calendar, FileText, DollarSign, Check, RefreshCw, ArrowDownRight, Percent, Printer, X } from "lucide-react";

const FluxoCaixa = () => {
    const user = useSelector((state) => state.user.user);
    const language = useSelector((state) => state.language?.currentLanguage || "pt-AO");
    const empresaId = user?.empresa_id;

    const baseURL = api.defaults.baseURL || "http://localhost:3000";

    const hojeStr = new Date().toISOString().split('T')[0];
    const [dataInicio, setDataInicio] = useState(hojeStr);
    const [dataFim, setDataFim] = useState(hojeStr);
    
    const [vendas, setVendas] = useState([]);
    const [totaisPeriodo, setTotaisPeriodo] = useState({ 
        totalVendasBruto: 0, 
        totalVendasLiquido: 0,
        totalIvaRetido: 0,
        totalRetencaoFonte: 0,
        totalDespesasAcumulado: 0,
        saldoFinalPeriodo: 0
    });
    const [loading, setLoading] = useState(false);

    // 🚀 ESTADOS PARA OPERAÇÃO DE IMPRESSÃO TÉRMICA DE FECHAMENTO
    const [modalAberto, setModalAberto] = useState(false);
    const [impressoras, setImpressoras] = useState([]);
    const [loadingImpressoras, setLoadingImpressoras] = useState(false);
    const [loadingImpressao, setLoadingImpressao] = useState(false);

    const texts = {
        "pt-AO": {
            reportTitle: "Demonstração de Fluxo de Caixa",
            reportSubtitle: "Análise cronológica unificada de entradas de faturação e saídas de despesas operacionais",
            lblStart: "Data Inicial",
            lblEnd: "Data Final",
            btnFilter: "Filtrar",
            btnPdf: "Imprimir Relatório",
            btnTicket: "Imprimir Ticket",
            cardGross: "Faturamento Bruto",
            cardNet: "Total Líquido (Entradas)",
            cardExpenses: "Despesas no Período",
            cardBalance: "Saldo do Período",
            colId: "ID",
            colDoc: "DOCUMENTO",
            colCliente: "CLIENTE / DESCRIÇÃO",
            colBruto: "VALOR BRUTO",
            colIva: "IVA",
            colRetencao: "RET. FONTE",
            colLiquido: "VALOR LÍQUIDO",
            colData: "DATA EMISSÃO",
            modalTitle: "Selecionar Impressora de Caixa",
            modalSubtitle: "Escolha uma impressora térmica ativa para emitir o resumo do fluxo de caixa",
            btnEnviarImpressao: "Imprimir Agora",
            lblNenhumaImpressora: "Nenhuma impressora térmica encontrada para esta empresa."
        },
        "en-US": {
            reportTitle: "Cash Flow Statement",
            reportSubtitle: "Unified chronological analysis of invoicing entries and operational expense outflows",
            lblStart: "Start Date",
            lblEnd: "End Date",
            btnFilter: "Filter",
            btnPdf: "Print Report",
            btnTicket: "Print Ticket",
            cardGross: "Gross Invoicing",
            cardNet: "Total Net (Inflow)",
            cardExpenses: "Expenses in Period",
            cardBalance: "Period Balance",
            colId: "ID",
            colDoc: "DOCUMENT",
            colCliente: "CUSTOMER / DESCRIPTION",
            colBruto: "GROSS VALUE",
            colIva: "TAX (VAT)",
            colRetencao: "WHT TAX",
            colLiquido: "NET VALUE",
            colData: "ISSUE DATE",
            modalTitle: "Select Cash Printer",
            modalSubtitle: "Choose an active thermal printer to output the cash flow summary",
            btnEnviarImpressao: "Print Now",
            lblNenhumaImpressora: "No thermal printer found for this company."
        }
    };

    const t = texts[language] || texts["pt-AO"];

    useEffect(() => {
        if (empresaId) {
            fetchVendasPeriodo();
        }
    }, [empresaId]);

    const fetchVendasPeriodo = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/app/relatorios/periodo/${empresaId}`, {
                params: {
                    data_inicio: dataInicio,
                    data_fim: dataFim
                }
            });
            if (response.data) {
                const listaVendas = response.data.dados?.vendas || [];
                const listaDespesas = response.data.dados?.despesas || [];

                const despesasFormatadas = listaDespesas.map(d => ({
                    id: d.id,
                    isDespesa: true,
                    doc_type: "DESPESA",
                    numero: d.descricao ? d.descricao.toUpperCase() : "DESPESA OPERACIONAL",
                    Cliente: { nome: "CLIENTE GERAL" }, 
                    total_geral: d.valor,
                    total_iva: 0,
                    retencao_fonte: 0,
                    liquido_real: d.valor,
                    createdAt: d.data ? `${d.data}T00:00:00.000Z` : new Date().toISOString()
                }));

                const fluxoUnificado = [...listaVendas, ...despesasFormatadas].sort((a, b) => 
                    new Date(b.createdAt) - new Date(a.createdAt)
                );

                setVendas(fluxoUnificado);
                
                setTotaisPeriodo(response.data.totais || { 
                    totalVendasBruto: 0, 
                    totalVendasLiquido: 0,
                    totalIvaRetido: 0,
                    totalRetencaoFonte: 0,
                    totalDespesasAcumulado: 0,
                    saldoFinalPeriodo: 0
                });
            }
        } catch (error) {
            console.error("Erro ao buscar fluxo de caixa por período:", error);
        } finally {
            setLoading(false);
        }
    };

    // 🚀 MAPEA EQUIPAMENTOS TÉRMICOS DA EMPRESA
    const abrirModalImpressora = async () => {
        if (vendas.length === 0) return;
        setModalAberto(true);
        setLoadingImpressoras(true);
        try {
            const res = await api.get(`/app/impressoras/empresa/${empresaId}`);
            setImpressoras(res.data || []);
        } catch (error) {
            console.error("Erro ao mapear parque de impressoras:", error);
        } finally {
            setLoadingImpressoras(false);
        }
    };

    // 🚀 ENVIA O SPOOL DE FECHAMENTO DE CAIXA / RESUMO UNIFICADO
    const dispararImpressaoTicket = async (impressoraId) => {
        setLoadingImpressao(true);
        try {
            await api.post(`/app/prints/ticket-resumo-caixa`, {
                empresa_id: empresaId,
                data_inicio: dataInicio,
                data_fim: dataFim,
                impressora_id: impressoraId
            });
            setModalAberto(false);
        } catch (error) {
            console.error("Erro ao processar spool térmico de caixa:", error);
            alert("Falha ao comunicar com o spooler da impressora de caixa selecionada.");
        } finally {
            setLoadingImpressao(false);
        }
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat("pt-AO", { style: "currency", currency: "AOA" }).format(val || 0);
    };

    // Link dinâmico para renderização e download do PDF de Tesouraria
    const printUrl = `${baseURL}/prints/fluxo-de-caixa?empresa_id=${empresaId}&data_inicio=${dataInicio}&data_fim=${dataFim}`;

    return (
        <div id="layout-wrapper">
            <Navbar />
            <Sidebar />

            <style>{`
                .win10-layout { min-height: 90vh; padding: 0px 0px; }
                .win10-panel-title { font-size: 18px; color: #323130; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
                .win10-card-main { background: #fff; border: 1px solid #e1e1e1; border-radius: 0px; padding: 20px; margin-bottom: 25px; }
                
                /* Grid de KPIs Dinâmicos */
                .kpi-container { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
                .kpi-box { background: #faf9f8; border: 1px solid #d2d0ce; padding: 15px 18px; flex: 1; min-width: 220px; border-left: 4px solid #0078d4; }
                .kpi-box.kpi-liquido { border-left-color: #107c41; }
                .kpi-box.kpi-despesa { border-left-color: #a80000; }
                .kpi-box.kpi-saldo { border-left-color: #323130; }
                .kpi-box.kpi-saldo-negativo { border-left-color: #e81123; }
                .kpi-title { font-size: 11px; color: #605e5c; text-transform: uppercase; font-weight: bold; }
                .kpi-value { font-size: 20px; color: #323130; font-weight: 700; font-family: monospace; margin-top: 4px; }

                .win10-input { border-radius: 0px; border: 1px solid #8a8886; font-size: 13px; padding: 5px 10px; height: 34px; background: #fff; }
                .win10-input:focus { border-color: #0078d4; box-shadow: none; }
                
                .win10-btn-filter { background: #323130; color: #fff; border-radius: 0px; font-size: 12px; font-weight: 600; height: 34px; padding: 0 20px; border: none; text-transform: uppercase; transition: 0.1s; display: inline-flex; align-items: center; gap: 6px; }
                .win10-btn-filter:hover { background: #201f1e; }

                /* Botões de Ações de Exportação e Spool */
                .win10-link-pdf { background: #a80000; color: #fff !important; border-radius: 0px; font-size: 12px; font-weight: 600; height: 34px; padding: 0 20px; display: inline-flex; align-items: center; gap: 8px; text-transform: uppercase; text-decoration: none; transition: 0.1s; border: none; }
                .win10-link-pdf:hover { background: #800000; text-decoration: none; }
                .win10-link-pdf.disabled { background: #f3f2f1; color: #a19f9d !important; cursor: not-allowed; pointer-events: none; }

                .win10-btn-ticket { background: #107c41; color: #fff; border-radius: 0px; font-size: 12px; font-weight: 600; height: 34px; padding: 0 20px; display: inline-flex; align-items: center; gap: 8px; text-transform: uppercase; transition: 0.1s; border: none; cursor: pointer; }
                .win10-btn-ticket:hover { background: #0b592e; }
                .win10-btn-ticket:disabled { background: #f3f2f1; color: #a19f9d; cursor: not-allowed; }

                /* Badges */
                .badge-doc { font-size: 10px; padding: 3px 8px; font-weight: bold; text-transform: uppercase; color: #fff; display: inline-block; }
                .badge-doc.doc-ft { background: #7a24b3; }
                .badge-doc.doc-fr { background: #107c41; }
                .badge-doc.doc-vd { background: #0078d4; }
                .badge-doc.doc-nc { background: #a80000; }
                .badge-doc.doc-despesa { background: #e81123; }

                /* MODAL WINDOWS 10 STYLE */
                .win10-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 10050; }
                .win10-modal-box { background: #fff; border: 1px solid #0078d4; min-width: 450px; box-shadow: 0 4px 15px rgba(0,0,0,0.2); border-radius: 0px; padding: 0; }
                .win10-modal-header { background: #f3f2f1; padding: 10px 15px; border-bottom: 1px solid #e1e1e1; display: flex; justify-content: space-between; align-items: center; }
                .win10-modal-body { padding: 20px; }
                .printer-row { padding: 10px; border: 1px solid #d2d0ce; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center; transition: 0.1s; }
                .printer-row:hover { border-color: #0078d4; background: #f3f2f1; }
            `}</style>

            <main className="app-wrapper animate__animated animate__fadeIn">
                <div className="win10-layout">
                    
                    <div className="win10-card-main shadow-sm">
                        {/* Filtros e Títulos */}
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4 border-bottom pb-3">
                            <div>
                                <h2 className="win10-panel-title m-0 d-flex align-items-center gap-2">
                                    <FileText size={20} className="text-dark"/> {t.reportTitle}
                                </h2>
                                <p className="text-muted small m-0 mt-1">{t.reportSubtitle}</p>
                            </div>
                            
                            <div className="d-flex align-items-end gap-2 flex-wrap">
                                <div className="d-flex flex-column">
                                    <label className="text-muted small fw-bold mb-1">{t.lblStart}</label>
                                    <input type="date" className="form-control win10-input" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
                                </div>
                                <div className="d-flex flex-column">
                                    <label className="text-muted small fw-bold mb-1">{t.lblEnd}</label>
                                    <input type="date" className="form-control win10-input" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
                                </div>

                                <button className="win10-btn-filter" onClick={fetchVendasPeriodo} disabled={loading}>
                                    {loading ? <RefreshCw size={14} className="animate-spin" /> : <Calendar size={14}/>} 
                                    {t.btnFilter}
                                </button>

                                {/* EMISSÃO PDF RELATÓRIO FINANCEIRO */}
                                <a 
                                    href={printUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className={`win10-link-pdf ${vendas.length === 0 ? "disabled" : ""}`}
                                >
                                    <FileText size={14}/>
                                    {t.btnPdf}
                                </a>

                                {/* 🚀 EMISSÃO TICKET TÉRMICO DE CAIXA */}
                                <button 
                                    className="win10-btn-ticket"
                                    onClick={abrirModalImpressora}
                                    disabled={vendas.length === 0}
                                >
                                    <Printer size={14}/>
                                    {t.btnTicket}
                                </button>
                            </div>
                        </div>

                        {/* Expandido para 4 Quadrantes Analíticos */}
                        <div className="kpi-container">
                            <div className="kpi-box">
                                <div className="kpi-title d-flex align-items-center gap-1"><DollarSign size={13}/> {t.cardGross}</div>
                                <div className="kpi-value">{formatCurrency(totaisPeriodo.totalVendasBruto)}</div>
                            </div>
                            <div className="kpi-box kpi-liquido">
                                <div className="kpi-title d-flex align-items-center gap-1"><Check size={13}/> {t.cardNet}</div>
                                <div className="kpi-value">{formatCurrency(totaisPeriodo.totalVendasLiquido)}</div>
                            </div>
                            <div className="kpi-box kpi-despesa">
                                <div className="kpi-title d-flex align-items-center gap-1"><ArrowDownRight size={13}/> {t.cardExpenses}</div>
                                <div className="kpi-value">{formatCurrency(totaisPeriodo.totalDespesasAcumulado)}</div>
                            </div>
                            <div className={`kpi-box kpi-saldo ${totaisPeriodo.saldoFinalPeriodo < 0 ? 'kpi-saldo-negativo' : ''}`}>
                                <div className="kpi-title d-flex align-items-center gap-1"><Percent size={13}/> {t.cardBalance}</div>
                                <div className="kpi-value">{formatCurrency(totaisPeriodo.saldoFinalPeriodo)}</div>
                            </div>
                        </div>

                        {/* Render da Tabela de Fluxo de Caixa Misto */}
                        <div className="mt-3">
                            <DataTable 
                                columns={[
                                    { title: t.colId, data: "id" },
                                    { 
                                        title: t.colDoc, 
                                        data: "numero",
                                        render: (numero, row) => {
                                            if (row.isDespesa) {
                                                return `<span class="badge-doc doc-despesa">DESPESA</span>`;
                                            }
                                            const sufixo = numero ? numero.split('/').pop() : '---';
                                            let badgeClass = "doc-ft";
                                            
                                            if (row.doc_type === "FR") badgeClass = "doc-fr";
                                            if (row.doc_type === "VD") badgeClass = "doc-vd";
                                            if (row.doc_type === "NC") badgeClass = "doc-nc";

                                            return `<span class="badge-doc ${badgeClass}">${row.doc_type || 'FT'} ${sufixo}</span>`;
                                        }
                                    },
                                    { 
                                        title: t.colCliente, 
                                        data: "Cliente", 
                                        render: (Cliente, row) => {
                                            if (row.isDespesa) {
                                                return row.numero || "DESPESA OPERACIONAL";
                                            }
                                            return Cliente?.nome ? Cliente.nome.toUpperCase() : "CLIENTE GERAL";
                                        }
                                    },
                                    { 
                                        title: t.colBruto, 
                                        data: "total_geral", 
                                        render: (total, row) => {
                                            if (row.isDespesa) {
                                                return `<span class="font-mono text-danger">-${formatCurrency(total)}</span>`;
                                            }
                                            return `<span class="font-mono">${formatCurrency(total)}</span>`;
                                        }
                                    },
                                    { 
                                        title: t.colIva, 
                                        data: "total_iva", 
                                        render: (iva) => `<span class="font-mono text-muted">${formatCurrency(iva)}</span>`
                                    },
                                    { 
                                        title: t.colRetencao, 
                                        data: "retencao_fonte", 
                                        render: (ret) => {
                                            const valor = parseFloat(ret || 0);
                                            return `<span class="font-mono text-danger">${valor > 0 ? '-' : ''}${formatCurrency(valor)}</span>`;
                                        }
                                    },
                                    { 
                                        title: t.colLiquido, 
                                        data: "liquido_real", 
                                        render: (liq, row) => {
                                            if (row.isDespesa) {
                                                return `<strong class="font-mono text-danger">-${formatCurrency(liq)}</strong>`;
                                            }
                                            return `<strong class="font-mono text-success">${formatCurrency(liq)}</strong>`;
                                        }
                                    },
                                    { 
                                        title: t.colData, 
                                        data: "createdAt",
                                        render: (date) => date ? new Date(date).toLocaleString("pt-AO") : "---"
                                    }
                                ]}
                                data={vendas}
                            />
                        </div>
                    </div>

                </div>
            </main>

            {/* 🚀 MODAL WINDOWS 10 PARA SELEÇÃO DE IMPRESSORA DE POS / TERMICA */}
            {modalAberto && (
                <div className="win10-modal-overlay">
                    <div className="win10-modal-box">
                        <div className="win10-modal-header">
                            <div>
                                <h5 className="m-0 text-dark fw-bold" style={{fontSize: "14px"}}>{t.modalTitle}</h5>
                            </div>
                            <button className="btn p-0 border-0" onClick={() => setModalAberto(false)} disabled={loadingImpressao}>
                                <X size={18} className="text-muted"/>
                            </button>
                        </div>
                        <div className="win10-modal-body">
                            <p className="text-muted small mb-3">{t.modalSubtitle}</p>
                            
                            {loadingImpressoras ? (
                                <div className="text-center py-4">
                                    <RefreshCw size={24} className="animate-spin text-primary mx-auto" />
                                    <span className="text-muted small mt-2 d-block">Varrendo rede interna e mapeamentos...</span>
                                </div>
                            ) : impressoras.length === 0 ? (
                                <div className="alert alert-warning border-0 small rounded-0">{t.lblNenhumaImpressora}</div>
                            ) : (
                                <div style={{maxHeight: "250px", overflowY: "auto"}}>
                                    {impressoras.map((printer) => (
                                        <div key={printer.id} className="printer-row">
                                            <div>
                                                <div className="fw-bold text-dark small d-flex align-items-center gap-1">
                                                    <Printer size={13}/> {printer.nome.toUpperCase()}
                                                </div>
                                                <small className="text-muted" style={{fontSize: "11px"}}>Caminho/Porta: {printer.porta || "Local Raw USB"}</small>
                                            </div>
                                            <button 
                                                className="win10-btn-filter" 
                                                style={{height: "28px", fontSize: "11px", padding: "0 12px"}}
                                                onClick={() => dispararImpressaoTicket(printer.id)}
                                                disabled={loadingImpressao}
                                            >
                                                {loadingImpressao ? "Enviando..." : t.btnEnviarImpressao}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default FluxoCaixa;